import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import externalLinkGreyIcon from 'assets/images/icons/link-external-grey.svg';
import formGreyIcon from 'assets/images/icons/form-grey.svg';
import pdfFileGreyIcon from 'assets/images/icons/pdf-file-grey.svg';
import playWhiteIcon from 'assets/images/icons/play-white.svg';
import userGreyIcon from 'assets/images/icons/users.svg';
import AlertModal from 'components/common-components/AlertModal';
import DateTimePickerDropdown from 'components/common-components/DateTimePickerDropdown';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import NestedSelect from 'components/form-components/NestedSelect';
import TextField from 'components/form-components/TextField';
import UserAndGroupSelection from 'components/common-components/UserAndGroupSelection';
import { CallAPI } from 'actions/General';
import { priorityOptionsList } from '../utils';
import { getUserGroupDisplayValue } from 'pages/tasks/utils';

const TicketEditForm = ({
  selectedRecord,
  mediaUrls,
  actionsCallBack,
  companyFieldList,
  handleClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [values, setValues] = useState({
    referenceNumber: '',
    title: '',
    description: '',
    status: '',
    ticketType: '',
    priority: '',
    dueDate: null,
    formTitle: '',
    form: '',
    submissionRefNumber: '',
    questionTitle: '',
    companyFieldsData: '',
    location: '',
    createdAt: '',
    createdBy: '',
    users: [],
    groups: [],
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [mediaInfo, setMediaInfo] = useState(null);
  const [allUsersAndGroups, setAllUsersAndGroups] = useState(null);

  const handleSubmit = async () => {
    setTouched({});
    const payload = {
      ids: [selectedRecord._id],
      updatedDetails: {
        priority: values.priority,
        dueDate: values.dueDate ? values.dueDate : null,
        status: values.status,
        assignedTo: {
          users: values.users,
          groups: values.groups,
        },
      },
    };
    let result = await CallAPI('UPDATE_TICKETS', payload, null, setLoading);
    if (result.status) {
      toast.success(t('alert_success_ticket_updated'));
      handleClose();
      actionsCallBack();
    }
  };

  useEffect(() => {
    if (Object.keys(selectedRecord ?? {}).length > 0) {
      setValues({
        referenceNumber: selectedRecord.referenceNumber ?? '',
        title: selectedRecord.title ?? '',
        description: selectedRecord.description ?? '',
        status: selectedRecord.status ?? '',
        ticketType: selectedRecord.ticketType ?? '',
        priority: selectedRecord.priority ?? '',
        dueDate: selectedRecord.dueDate ?? '',
        formTitle: selectedRecord.formTitle ?? '',
        form: selectedRecord.form ?? '',
        submissionRefNumber: selectedRecord.submissionRefNumber ?? '',
        questionTitle: selectedRecord.questionTitle ?? '',
        companyFieldsData: selectedRecord.companyFieldsData ?? {},
        location: selectedRecord.location ?? {},
        createdAt: selectedRecord.createdAt ?? '',
        createdBy: selectedRecord.createdBy ?? '',
        users: (selectedRecord.assignedTo?.users ?? []).map((u) => u._id),
        groups: (selectedRecord.assignedTo?.groups ?? []).map((g) => g._id),
      });
    }
  }, [selectedRecord]);

  const handleChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
  };

  const ticketStatusOptions = [
    { value: 'open', displayValue: 'select_open' },
    { value: 'in-progress', displayValue: 'select_in_progress' },
    { value: 'resolved', displayValue: 'select_resolved' },
    { value: 'closed', displayValue: 'select_closed' },
  ];

  const getInitials = (user) => {
    if (!user) return '';
    const { firstName = '', lastName = '' } = user;
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  };
  const createdByinitials = getInitials(values.createdBy);

  const displayText = useMemo(() => {
    return getUserGroupDisplayValue(
      allUsersAndGroups?.users.filter((u) => values.users.includes(u.id)),
      allUsersAndGroups?.groups.filter((g) => values.groups.includes(g.id)),
    );
  }, [values.users, values.groups, allUsersAndGroups]);

  const handleAssignmentDoneAction = (data) => {
    setValues((prev) => ({ ...prev, users: data.users, groups: data.groups }));
  };

  return (
    <>
      <div className='d-flex flex-column gap-3 p-3'>
        <div className='d-flex align-items-center gap-3'>
          <div className='theme-font-inter-medium theme-background-grey-5 rounded-pill theme-text-black-1 py-1 px-2'>
            {`TID-${values.referenceNumber}`}
          </div>
          <img className='theme-height-20' src={formGreyIcon} />
        </div>

        <span className='theme-font-inter-semi-bold theme-size-1_5 theme-text-black-1'>
          {values.title}
        </span>

        <TextField
          name='description'
          classes='w-auto'
          value={values.description}
          handleChange={() => {}}
          placeholder='field_description'
          disabled
          shrinkFieldOnly
        />

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_status')}</span>
          <div className='col-8'>
            <NestedSelect
              options={ticketStatusOptions}
              name='status'
              handleChange={handleChange}
              toggleClasses='d-flex align-items-center theme-height-40 px-2 border-grey-1-h-1 width-max-content'
              toggleComponent={
                <>
                  <label className='cursor-pointer theme-text-black-1'>
                    {t(
                      ticketStatusOptions.find((o) => o.value === values.status)?.displayValue ??
                        'select_select',
                    )}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
              shrink
            />
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_type')}</span>
          <span className='col-8 theme-text-black-1'>{values.ticketType.name}</span>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_priority')}</span>
          <div className='col-8'>
            <NestedSelect
              options={priorityOptionsList}
              name='priority'
              handleChange={handleChange}
              toggleClasses='d-flex align-items-center theme-height-40 px-2 border-grey-1-h-1 width-max-content'
              toggleComponent={
                <>
                  <label className='cursor-pointer theme-text-black-1'>
                    {t(
                      priorityOptionsList.find((o) => o.value === values.priority)?.displayValue ??
                        'select_select',
                    )}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
              disableIconGrayscale
            />
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_due_date')}</span>
          <div className='col-8'>
            <DateTimePickerDropdown
              name='dueDate'
              value={values.dueDate ?? new Date().toISOString()}
              onChange={handleChange}
              format12Hour
            />
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_assigned_to')}</span>
          <div className='col-8'>
            <UserAndGroupSelection
              doneAction={handleAssignmentDoneAction}
              onDataLoad={setAllUsersAndGroups}
              toggleComponent={
                <div className='d-flex align-items-center gap-2'>
                  <img className='cursor-pointer theme-height-20' src={userGreyIcon} />
                  <span
                    className={`cursor-pointer ${
                      displayText ? 'theme-text-black-1' : 'theme-text-green-1'
                    }`}
                  >
                    {displayText ? displayText : t('text_assign_users')}
                  </span>
                </div>
              }
              existingUserIds={values.users}
              existingGroupIds={values.groups}
            />
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_form')}</span>
          <div className='col-8'>
            <div className='d-flex align-items-center gap-2'>
              <img className='theme-height-20' src={formGreyIcon} />
              <span className='theme-text-black-1'>{values.formTitle}</span>
              <img
                className='cursor-pointer theme-height-20'
                src={externalLinkGreyIcon}
                onClick={() => {
                  navigate('/forms/edit', {
                    relative: 'path',
                    state: { type: 'edit', formId: values.form },
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_ref_no')}</span>
          <span className='col-8 theme-text-black-1'>{values.submissionRefNumber}</span>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_question')}</span>
          <span className='col-8 theme-text-black-1'>{values.questionTitle}</span>
        </div>

        {values.companyFieldsData &&
          Object.entries(values.companyFieldsData).map(([key, value], i) => {
            const companyFieldTitle =
              companyFieldList.find((li) => li._id === key)?.fieldTitle ?? '';
            return (
              <div key={`companyField_${i}`} className='row align-items-center'>
                <span className='col-4'>{companyFieldTitle}</span>
                <span className='col-8 theme-text-black-1'>{value}</span>
              </div>
            );
          })}

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_location')}</span>
          <div className='col-8'>
            <div className='d-flex align-items-center gap-2'>
              <span className='theme-text-black-1'>
                {values.location.address || 'Not provided'}
              </span>
              {values.location.coordinates && (
                <img
                  className='cursor-pointer theme-height-20'
                  src={externalLinkGreyIcon}
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${values.location.coordinates}`;
                    window.open(url, '_blank');
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_created_date')}</span>
          <span className='col-8 theme-text-black-1'>
            {values.createdAt ? new Date(values.createdAt).toLocaleString('en-US') : ''}
          </span>
        </div>

        <div className='row align-items-center'>
          <span className='col-4'>{t('text_created_by')}</span>

          <div className='col-8 d-flex align-items-center gap-2'>
            {createdByinitials && (
              <span className='theme-text-black-1 theme-size-0_9 theme-background-grey-5 rounded-full p-1 user-select-none'>
                {createdByinitials}
              </span>
            )}
            <span className='theme-text-black-1'>
              {values.createdBy
                ? values.createdBy.firstName + ' ' + values.createdBy.lastName
                : 'N/A'}
            </span>
          </div>
        </div>

        <div className='d-flex flex-column gap-3'>
          <span>{t('text_attachments')}</span>

          {selectedRecord?.media?.length > 0 && (
            <div className='mb-2 ticket-attachment-container'>
              {selectedRecord.media.map((item, i) => {
                const mediaUrl = mediaUrls[item._id];
                const mediaType = item.mediaType;

                return (
                  <React.Fragment key={i}>
                    {(mediaType === 'image' || mediaType === 'file') && (
                      <div
                        className='thumbnail-div'
                        onClick={() => {
                          if (mediaType === 'file') {
                            window.open(mediaUrl);
                          } else {
                            setMediaInfo({
                              src: mediaUrl,
                              type: mediaType,
                            });
                          }
                        }}
                      >
                        <img
                          src={mediaType === 'file' ? pdfFileGreyIcon : mediaUrl}
                          className='imgThumbnail'
                          alt='attachment'
                        />
                      </div>
                    )}
                    {mediaType === 'video' && (
                      <div
                        className='thumbnail-div position-relative'
                        onClick={() => setMediaInfo({ src: mediaUrl, type: 'video' })}
                      >
                        <div className='black-overlay'></div>
                        <img
                          className='position-absolute play-icon'
                          src={playWhiteIcon}
                          alt='Play icon'
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-10-h-1'>
        <FormButton text={'Discard'} variant='white-1' onClick={handleClose} />
        <FormLoadingButton
          text='button_update'
          variant='green-1'
          loading={loading}
          disabled={!selectedRecord}
          onClick={handleSubmit}
        />
      </div>

      <AlertModal
        show={!!mediaInfo}
        actionButtons={[]}
        backdropClassName='backdrop-overlap'
        noPadding
        size='lg'
      >
        <div className='p-3 w-100'>
          {mediaInfo && (
            <>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <label className='theme-size-1_1'>
                  <span>{mediaInfo.type}</span>
                </label>
                <img
                  src={crossGreyIcon}
                  width={12}
                  className='cursor-pointer'
                  onClick={() => setMediaInfo(null)}
                />
              </div>
              <div className='d-flex justify-content-center align-items-center'>
                {mediaInfo.type === 'image' ? (
                  <img src={mediaInfo.src} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                ) : (
                  <video id='modal-vid' controls style={{ maxWidth: '100%', maxHeight: '70vh' }}>
                    <source id='modal-video-source' type='video/mp4' src={mediaInfo.src} />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </>
          )}
        </div>
      </AlertModal>
    </>
  );
};

TicketEditForm.propTypes = {
  selectedRecord: PropTypes.object,
  mediaUrls: PropTypes.object.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
  companyFieldList: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default TicketEditForm;
